import axios, { AxiosResponse } from "axios"
import { OpportunityStage } from "models/FullApplication"
import { isDevModeEnabled } from "lib/Cookie"
import * as DevModeService from "lib/DevMode/DevModeService"

export interface StatusApiResponse {
  recommendationAccepted: boolean
  numberOfDocumentsRequested: number
  setCompletionDateForEmail: string
  opportunityOwnerTextForEmail: string
  ownerEmailAddress: string
  property?: {
    firstLineAddress: string | null
    value: number | null
  }
}

export interface StatusData {
  recommendationAccepted: boolean
  documentsRequested: number
  setCompletionDateForEmail: string
  opportunityOwnerTextForEmail: string
  ownerEmailAddress: string
  flexRoles2?: string
  mipIssuedToCustomer?: boolean
  amount?: number | null
  stageName?: string
  opportunityRecordType1?: string
  propertyAddress?: string | null
  offerAmount?: number | null
  mipUrl?: string
  currentExperiments?: string | null
}

/**
 * Determines which application stages require additional API data
 */
export const getStagesThatRequireApiData = (): OpportunityStage[] => [
  OpportunityStage.quoted,
  OpportunityStage.closedlost,
  OpportunityStage.completionSet,
  OpportunityStage.mortgageready,
  OpportunityStage.online,
  OpportunityStage.coach,
]

/**
 * Checks if the given application status requires API data
 */
export const shouldFetchStatusData = (applicationStatus: OpportunityStage): boolean => {
  return getStagesThatRequireApiData().includes(applicationStatus)
}

/**
 * Fetches status data from the API
 */
export const fetchStatusFromApi = async (applicationId: string): Promise<StatusApiResponse> => {
  if (isDevModeEnabled()) {
    return await DevModeService.getStatus()
  } else {
    const response = (await axios.get(`${process.env.MY_MOJO_API}/status/${applicationId}`, {
      withCredentials: true,
    })) as AxiosResponse<StatusApiResponse>
    return response.data
  }
}

/**
 * Transforms raw API response data into the format expected by the component
 */
export const transformStatusData = (apiResponse: StatusApiResponse): StatusData => {
  const documentsRequested =
    apiResponse.numberOfDocumentsRequested > 0 && apiResponse.numberOfDocumentsRequested

  return {
    ...apiResponse,
    documentsRequested,
    propertyAddress: apiResponse.property?.firstLineAddress || null,
    offerAmount: apiResponse.property?.value || null,
  }
}
